<template>
  <CRow>
    <!-- ================= Update Role Modal ================= -->
    <CModal size="lg" title="Update Role" :show.sync="roleModal" :close-on-backdrop="false" color="primary">
      <div class="p-3">
        <label>Select Role</label>
        <select v-model="selectedRole" class="form-control">
          <option :value="1">Super Admin</option>
          <option :value="2">Batch Admin</option>
          <option :value="3">Member</option>
        </select>
      </div>
      <template #footer>
        <CButton color="secondary" @click="roleModal = false">Cancel</CButton>
        <CButton color="primary" :disabled="saving" @click="updateRole">Save</CButton>
      </template>
    </CModal>

    <!-- ================= Create Committee Modal ================= -->
    <CModal size="lg" title="Create Committee Member" :show.sync="committeeModal" :close-on-backdrop="false" color="primary">
      <div class="p-3">
        <!-- Basic member details only -->
        <div v-if="selectedMember" class="mb-3 p-2 border rounded bg-light">
          <div><strong>Name:</strong> {{ selectedMember.name || '-' }}</div>
          <div><strong>Email:</strong> {{ selectedMember.email || '-' }}</div>
          <div><strong>Mobile No:</strong> {{ selectedMember.mobileNo || '-' }}</div>
          <div><strong>Batch:</strong> {{ selectedMember.batch || '-' }}</div>
        </div>

        <div class="form-group">
          <label>Year <span class="text-danger">*</span></label>
          <select v-model="committeeForm.year" class="form-control">
            <option :value="null" disabled>Select Year</option>
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>

        <div class="form-group">
          <label>Committee Title <span class="text-danger">*</span></label>
          <select v-model="committeeForm.titleId" class="form-control" :disabled="titlesLoading">
            <option :value="null" disabled>{{ titlesLoading ? 'Loading...' : 'Select Title' }}</option>
            <option v-for="t in committeeTitles" :key="t.id" :value="t.id">{{ t.title }}</option>
          </select>
        </div>

        <div class="form-group">
          <label>Category</label>
          <input
            v-model="committeeForm.category"
            type="text"
            class="form-control"
            maxlength="191"
            placeholder="Enter category (optional)"
          />
        </div>

        <small v-if="committeeError" class="text-danger">{{ committeeError }}</small>
      </div>
      <template #footer>
        <CButton color="secondary" @click="committeeModal = false">Cancel</CButton>
        <CButton color="primary" :disabled="saving" @click="createCommittee">Save</CButton>
      </template>
    </CModal>

    <CCol col="12">
      <CCard>
        <CCardBody>
          <CAlert :color="messageColor" closeButton :show.sync="dismissCountDownS" fade>
            {{ alertMessage }}
          </CAlert>

          <v-server-table :columns="columns" :url="apiUrl" :options="options" ref="myTable">
            <template #status="data">
              <CBadge :color="statusInfo(data.row.status).color">{{ statusInfo(data.row.status).label }}</CBadge>
            </template>
            <template #role="data">{{ roleName(data.row) }}</template>
            <template #actions="data">
              <CButton color="primary" size="sm" class="mr-1 mb-1" @click="openRoleModal(data.row)">
                Update Role
              </CButton>
              <CButton
                v-if="statusInfo(data.row.status).label !== 'Pending' && !isCommitteeMember(data.row)"
                color="success"
                size="sm"
                class="mb-1"
                @click="openCreateCommittee(data.row)"
              >
                Create Committee
              </CButton>
            </template>
          </v-server-table>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Vue from "vue";
import {Mixin} from "/src/mixins/index.js";
import {ServerTable} from "vue-tables-2";
Vue.use(ServerTable, {}, false);

const ROLES = {1: "Super Admin", 2: "Batch Admin", 3: "Member"};

export default {
  name: "InstituteChainMember",
  mixins: [Mixin],
  data() {
    return {
      messageColor: "success",
      dismissCountDownS: 0,
      alertMessage: "",
      saving: false,

      committeeTitles: [],
      titlesLoading: false,
      committeeError: "",
      committeeForm: {id: null, memberId: null, year: null, titleId: null, category: ""},

      apiUrl: "",
      columns: ["name", "email", "mobileNo", "batch", "status", "role", "actions"],
      options: {
        headings: {
          name: "Name",
          email: "Email",
          mobileNo: "Mobile No",
          batch: "Batch",
          status: "Status",
          role: "Role",
          actions: "Action",
        },
        sortable: ["name", "email"],
        filterable: ["name", "email"],
      },

      roleModal: false,
      editId: "",
      selectedRole: null,

      committeeModal: false,
      selectedMember: null,
    };
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current + 1; y >= current - 10; y--) years.push(y);
      return years;
    },
  },
  created() {
    this.apiUrl = "/institute-chain/list-member/" + this.$route.params.id;
  },
  methods: {
    /* ---------- Helpers ---------- */
    roleName(row) {
      return ROLES[row.roleId || row.role_id] || "-";
    },
    isCommitteeMember(row) {
      const v = row.isCommitteeMember;
      return v === true || v === 1 || v === "1" || v === "true";
    },
    statusInfo(status) {
      const s = String(status === null || status === undefined ? "" : status).toLowerCase();
      if (s === "1" || s === "approved") return {label: "Approved", color: "success"};
      if (s === "0" || s === "pending") return {label: "Pending", color: "warning"};
      if (s === "2" || s === "blocked") return {label: "Blocked", color: "danger"};
      if (s === "3" || s === "removed") return {label: "Removed", color: "secondary"};
      if (s === "4" || s === "rejected") return {label: "Rejected", color: "danger"};
      return {label: status || "-", color: "info"};
    },
    showAlert(message, color = "success") {
      this.alertMessage = message;
      this.messageColor = color;
      this.dismissCountDownS = 10;
    },
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    /** Error only when the API says so; a plain { message } is a success text */
    isErrorResponse(response) {
      const d = response.data || {};
      return d.status === false || d.success === false || !!d.error;
    },
    successText(response, fallback) {
      const d = response.data || {};
      if (typeof d.success === "string") return d.success;
      if (typeof d.message === "string") return d.message;
      return fallback;
    },
    /** Empty / whitespace -> null */
    cleanCategory(value) {
      const v = (value === null || value === undefined ? "" : String(value)).trim();
      return v === "" ? null : v;
    },
    refreshTable() {
      if (this.$refs.myTable) this.$refs.myTable.refresh();
    },
    fetchCommitteeTitles() {
      if (this.committeeTitles.length || this.titlesLoading) return;
      this.titlesLoading = true;
      axios
        .get("/institute-chain/title")
        .then((response) => {
          const raw = response.data && (response.data.data || response.data);
          this.committeeTitles = (Array.isArray(raw) ? raw : []).map((t) => ({
            id: t.id,
            title: t.title || t.name,
          }));
          if (!this.committeeTitles.length) this.committeeError = "No committee titles found.";
        })
        .catch(() => (this.committeeError = "Could not load committee titles."))
        .finally(() => (this.titlesLoading = false));
    },

    /* ---------- Role ---------- */
    openRoleModal(row) {
      this.editId = row.id;
      this.selectedRole = row.roleId || row.role_id;
      this.roleModal = true;
    },
    updateRole() {
      this.saving = true;
      axios
        .post("/institute-chain/update-role", {id: this.editId, roleId: this.selectedRole})
        .then((response) => {
          if (this.isErrorResponse(response)) {
            this.showAlert(this.successText(response, "Failed to update role"), "danger");
            return;
          }
          this.roleModal = false;
          this.showAlert(this.successText(response, "Role updated successfully"));
          this.refreshTable();
        })
        .catch((error) => this.showAlert(this.errorMessage(error, "Failed to update role"), "danger"))
        .finally(() => (this.saving = false));
    },

    /* ---------- Create committee ---------- */
    openCreateCommittee(row) {
      this.committeeError = "";
      this.selectedMember = {name: row.name, email: row.email, mobileNo: row.mobileNo, batch: row.batch};
      this.committeeForm = {
        id: null,
        memberId: row.id,
        year: new Date().getFullYear(),
        titleId: null,
        category: "",
      };
      this.committeeModal = true;
      this.fetchCommitteeTitles();
    },
    createCommittee() {
      const f = this.committeeForm;
      if (!f.year || !f.titleId) {
        this.committeeError = "Please select year and committee title.";
        return;
      }
      this.committeeError = "";
      this.saving = true;
      axios
        .post("/institute-chain/create-committee", {
          collegeAlumniNetworkIds: String(f.memberId),
          titleId: f.titleId,
          year: f.year,
          category: this.cleanCategory(f.category),
        })
        .then((response) => {
          if (this.isErrorResponse(response)) {
            this.committeeError = this.successText(response, "Failed to create committee member");
            return;
          }
          // Done: close the modal and reload the list
          this.committeeModal = false;
          this.showAlert(this.successText(response, "Committee member created. See the Committee tab."));
          this.refreshTable();
        })
        .catch((error) => (this.committeeError = this.errorMessage(error, "Failed to create committee member")))
        .finally(() => (this.saving = false));
    },
  },
};
</script>